import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack,
  Autocomplete,
  Avatar,
} from '@mui/material';
import {
  Add,
  Edit,
  EditNote,
  Delete,
  AccessTimeOutlined,
  Remove,
  Close,
} from '@mui/icons-material';

import { CommercialACForm } from '../../../components/common/CommercialAcfrom';
import { AMCForm } from '../../../components/common/AmcForm';
import { ProblemReportForm } from '../../../components/common/Others';
import { GetAdresses, GETUsers } from '../../../services/users';
import {
  IService,
  IUser,
  IUserAddress,
} from '../../../utils/entities/interface';
import { capitalizeWords, formatPhoneNumber } from '../../../utils/helper';
import { GETServices } from '../../../services/service';
import {
  EditBooking,
  GETBookingById,
  NewBooking,
} from '../../../services/booking';
import useSnackbar from '../../../hooks/useSnackbar';
import { useNavigate, useParams } from 'react-router-dom';
import EditAddressDialog from './EditAddress';
import CreateUserModal from './AddUserModal';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../components/atoms/Button';
import { DetailPageHeader } from '../../../components/layout/DetailPageHeader';
import ThemedDateField from '../../../components/atoms/ThemedDateField';
import { acItems } from '../../../utils/constants';
import {
  getBookingComponents,
  getBookingJobTypes,
  getBookingAppliances,
  getBookingAttributeOptions,
  SbAppliance,
  SbComponent,
} from '../../../services/serviceBooking';
import { ServiceRow } from '../../../components/organisms/bsesrvices';

// One option for the "Other" intake dropdowns
type IntakeOption = { value: string; label: string };

const removeZeroQuantityEntries = (
  acList: Record<string, number>,
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(acList || {}).filter(
      ([, value]) => Number(value) > 0,
    ),
  );

const AddBooking = () => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [userList, setUserList] = useState<IUser[] | null>(null);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [addresslist, setAddressList] = useState<IUserAddress[] | null>(null);
  const [openAddCustomer, setOpenAddCustomer] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<IUserAddress | null>(
    null,
  );
  const [isLoading, setisLoading] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [bookingDate, setBookingDate] = useState(() => {
    const today = new Date().toISOString().split('T')[0]; // Get current date in 'YYYY-MM-DD' format
    return today;
  });
  const [selectedSlot, setSelectedSlot] = useState<
    'FIRST_HALF' | 'SECOND_HALF'
  >('FIRST_HALF');
  const [selectedServices, setSelectedServices] = useState<
    { service: IService; serviceDetails: any }[]
  >([]);
  const [inputValue, setInputValue] = useState('');

  const isEditMode = Boolean(bookingId);

  useEffect(() => {
    fetchUserList();
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      fetchAddressList();
    }
  }, [selectedUser]);

  useEffect(() => {
    if (bookingId) fetchBookingDetails(bookingId);
  }, [bookingId]);

  const fetchBookingDetails = async (id: string) => {
    try {
      const { data } = await GETBookingById(id);

      const serviceAggregation: Record<string, any> = {};
      const selectedServicesData: any[] = [];

      if (Array.isArray(data.serviceDetails)) {
        for (const details of data.serviceDetails) {
          const key = details.service_data.key;
          const acType = details.acType?.toUpperCase();
          const quantity = parseInt(details.quantity, 10) || 0;

          // Handle special services separately
          if (
            ['COMMERCIAL_AC', 'COPPER_PIPING', 'AMC', 'OTHER'].includes(key)
          ) {
            const existingService = selectedServicesData.find(
              (s) => s.service.key === key,
            );

            if (existingService) {
              if (key === 'COMMERCIAL_AC') {
                existingService.serviceDetails.acList[acType] =
                  (existingService.serviceDetails.acList[acType] || 0) +
                  quantity;
              } else if (key === 'OTHER') {
                existingService.serviceDetails.problem = details.otherService;
                existingService.serviceDetails.comments = details.comment;
                existingService.serviceDetails.acType = details.acType || '';
              } else {
                existingService.serviceDetails.quantity += quantity;
              }
            } else {
              selectedServicesData.push({
                service: details.service_data,
                serviceDetails:
                  key === 'COMMERCIAL_AC'
                    ? {
                        acList: { [acType]: quantity },
                        services: details.services,
                      }
                    : key === 'OTHER'
                      ? {
                          problem: details.otherService,
                          comments: details.comment,
                          acType: details.acType || '',
                        }
                      : {
                          quantity: quantity,
                          place: details.place,
                          comments: details.comment,
                        },
              });
            }
            continue;
          }

          // Process normal services and group by `key`
          if (!serviceAggregation[key]) {
            serviceAggregation[key] = {
              service: details.service_data,
              serviceDetails: {}, // Change to an object instead of an array
            };
          }

          // "Other" intake lines (acType === 'Other') re-hydrate into __others__.
          if (details.acType === 'Other') {
            const arr: OtherEntry[] =
              serviceAggregation[key].serviceDetails[OTHER_KEY] || [];
            arr.push({
              appliance: details.serviceType || '',
              acType: details.place || '',
              problems: details.otherService
                ? String(details.otherService)
                    .split(',')
                    .map((s: string) => s.trim())
                    .filter(Boolean)
                : [],
              detail: details.comment || '',
            });
            serviceAggregation[key].serviceDetails[OTHER_KEY] = arr;
            continue;
          }

          // Rebuild the picker's composite key "<Appliance> ▸ <Component>" so
          // counters re-hydrate correctly. Preserve original case (catalog
          // labels like "Wall Mount" are case-sensitive). Legacy lines with no
          // serviceType collapse to just the component name.
          const componentName = details.acType || '';
          const mapKey = details.serviceType
            ? `${details.serviceType}${APPL_SEP}${componentName}`
            : componentName;
          if (serviceAggregation[key].serviceDetails[mapKey]) {
            serviceAggregation[key].serviceDetails[mapKey] += quantity;
          } else {
            serviceAggregation[key].serviceDetails[mapKey] = quantity;
          }
        }
      }

      // Convert grouped services to an array
      selectedServicesData.push(...Object.values(serviceAggregation));

      setSelectedServices(selectedServicesData);
      setSelectedSlot(data.slot);
      setSelectedUser(data.user);
      setSelectedAddress(data.addressDetails[0]);
      setBookingDate(new Date(data.date).toISOString().split('T')[0]);

      setAmount(data.amount);
    } catch (error) {
      console.error('Error fetching booking details:', error);
    }
  };

  const fetchAddressList = async () => {
    if (selectedUser && !isEditMode) {
      const fetched = await GetAdresses(selectedUser?._id);
      setAddressList(fetched.data);
      setSelectedAddress(fetched?.data?.[0]);
    }
  };

  const fetchUserList = async (search?: string) => {
    try {
      if (isEditMode) return;
      const obj: any = { limit: 50 };

      if (search && search.trim() !== '') {
        obj.searchQuery = search.trim();
      }
      const userList = await GETUsers(obj);
      setUserList(userList.data);
    } catch (error: any) {
      console.log('error', error);
    }
  };

  const resetBooking = () => {
    setSelectedUser(null);
    setOpenEditModal(false);
    setOpenAddCustomer(false);
    setSelectedAddress(null);
    setSelectedSlot('FIRST_HALF');
    setBookingDate(new Date().toISOString().split('T')[0]);
    setSelectedServices([]);
  };

  const handleCreateBooking = async () => {
    try {
      if (
        !selectedUser ||
        !selectedUser._id ||
        !selectedAddress ||
        !bookingDate
      ) {
        showSnackbar('Requried Information Missing', 'error');
        return;
      }
      setisLoading(true);

      const invalidOtherService = selectedServices.some(
        (ser) => ser.service.key === 'OTHER' && !ser.service._id,
      );
      if (invalidOtherService) {
        showSnackbar(
          'Other service is not configured. Please contact admin.',
          'error',
        );
        setisLoading(false);
        return;
      }

      const temp: any[] = [];
      selectedServices.map((ser) => {
        if (
          [
            'SERVICE',
            'REPAIR',
            'INSTALLATION',
            'COMPRESSOR',
            'GAS_CHARGING',
          ].includes(ser.service.key)
        ) {
          Object.entries(removeZeroQuantityEntries(ser.serviceDetails)).map(
            ([type, count]) => {
            // key may be "<Appliance> ▸ <Component>" (new) or just a name (legacy)
            const [appliancePart, componentPart] = String(type).split(APPL_SEP);
            const acType = componentPart ?? appliancePart;
            const serviceType = componentPart ? appliancePart : '';
            temp.push({
              service_id: ser.service._id,
              service_data: ser.service,
              services: [],
              serviceType,
              quantity: count,
              acType,
              place: '',
              comment: '',
              otherService: '',
            });
          });
          // "Other" intakes -> one line each. acType='Other' is the marker;
          // place=appliance-type, otherService=problems, comment=detail.
          const otherEntries = Array.isArray(ser.serviceDetails?.[OTHER_KEY])
            ? ser.serviceDetails[OTHER_KEY]
            : [];
          otherEntries.forEach((o: OtherEntry) => {
            temp.push({
              service_id: ser.service._id,
              service_data: ser.service,
              services: [],
              serviceType: o.appliance || '',
              quantity: 1,
              acType: 'Other',
              place: o.acType || '',
              comment: o.detail || '',
              otherService: (o.problems || []).join(', '),
            });
          });
        }
        if (ser.service.key == 'COMMERCIAL_AC') {
          Object.entries(
            removeZeroQuantityEntries(ser.serviceDetails.acList),
          ).map(([type, count]) => {
            temp.push({
              service_id: ser.service._id,
              service_data: ser.service,
              services: ser.serviceDetails.services,
              serviceType: '',
              quantity: count,
              acType: type,
              place: '',
              comment: '',
              otherService: '',
            });
          });
        }
        if (ser.service.key == 'AMC') {
          temp.push({
            service_id: ser.service._id,
            service_data: ser.service,
            services: ser.serviceDetails.services,
            serviceType: '',
            quantity: ser.serviceDetails.quantity,
            acType: '',
            place: ser.serviceDetails.place,
            comment: ser.serviceDetails.comments,
            otherService: '',
          });
        }
        if (ser.service.key == 'COPPER_PIPING') {
          temp.push({
            service_id: ser.service._id,
            service_data: ser.service,
            services: ser.serviceDetails.services,
            serviceType: '',
            quantity: ser.serviceDetails.quantity,
            acType: '',
            place: ser.serviceDetails.place,
            comment: ser.serviceDetails.comments,
            otherService: '',
          });
        }
        if (ser.service.key == 'OTHER') {
          temp.push({
            service_id: ser.service._id,
            service_data: ser.service,
            services: [],
            serviceType: '',
            quantity: ser.serviceDetails.quantity ?? 1,
            acType: ser.serviceDetails.acType || '',
            place: '',
            comment: ser.serviceDetails.comments,
            otherService: ser.serviceDetails.problem,
          });
        }
      });

      if (temp.length <= 0) {
        showSnackbar('Booking Service Infomation Required', 'error');
        setisLoading(false);
        return;
      }

      const missingServiceId = temp.some((item) => !item.service_id);
      if (missingServiceId) {
        showSnackbar(
          'Service information is incomplete. Please re-add the service.',
          'error',
        );
        setisLoading(false);
        return;
      }

      const status = isEditMode
        ? await EditBooking({
            bookingId: bookingId as string,
            amount: amount,
            serviceDetails: temp,
            slot: selectedSlot,
            date: bookingDate,
            addressId: selectedAddress?._id,
          })
        : await NewBooking({
            user_id: selectedUser?._id,
            serviceDetails: temp,
            date: bookingDate,
            amount: 0,
            addressId: selectedAddress?._id,
            slot: selectedSlot,
            name: selectedUser.name || 'N/A',
          });
      setisLoading(false);

      console.log('status', status);
      if (status == 201) {
        showSnackbar('Booking Created', 'success');
        setTimeout(() => {
          navigate(-1);
        }, 500);
      }
      if (status == 200) {
        showSnackbar('Booking Edited Successfully', 'success');
        setTimeout(() => {
          navigate(-1);
        }, 500);
      }
      return;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save booking';
      showSnackbar(message, 'error');
    } finally {
      setisLoading(false);
    }
  };

  const reloadAddress = (updatedAddress: IUserAddress) => {
    if (updatedAddress && updatedAddress._id) {
      setSelectedAddress(updatedAddress);
      setOpenEditModal(false);
    } else {
      fetchAddressList();
      setOpenEditModal(false);
    }
  };

  const handleAddedCustomer = () => {
    fetchUserList();
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchUserList(inputValue);
    }, 400);

    return () => clearTimeout(delay);
  }, [inputValue]);

  return (
    <Box sx={{ background: '#121212', minHeight: '100vh', color: '#fff' }}>
      {SnackbarComponent}
      {/* Header Section */}
      <DetailPageHeader title={isEditMode ? 'Edit Booking' : 'Create Booking'} />
      <Box sx={{ mb: 2 }} />
      <Box
        sx={{
          background: '#1C1C1C',
          borderRadius: 4,
          p: 7,
          width: '100%',
          margin: 3,
        }}
      >
        <Grid container spacing={2}>
          {/* Select User */}
          {isEditMode && selectedUser ? (
            <ViewCustomer Customer={selectedUser} />
          ) : (
            <>
              {' '}
              <Grid item xs={12}>
                <Typography color="#8A8D91">Existing User</Typography>
              </Grid>
              <Grid item xs={10} p={2}>
                <Autocomplete
                  options={Array.isArray(userList) ? userList : []}
                  getOptionLabel={(option) =>
                    `${capitalizeWords(option.name) || 'N/A'} (${option.phoneNumber.slice(0, 10)})`
                  } // Display name and phone number
                  value={selectedUser}
                  onChange={(event, newValue) => {
                    setSelectedUser(newValue);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  disabled={isEditMode}
                  sx={{
                    flex: 1,
                    '& .MuiAutocomplete-listbox': {
                      '&::-webkit-scrollbar': {
                        display: 'none', // Hides scrollbar for webkit browsers
                      },
                      '&::-moz-scrollbar': {
                        display: 'none', // Hides scrollbar for Firefox
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select User"
                      sx={{
                        input: { color: '#8A8D91' },
                        label: { color: '#8A8D91' },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                />
              </Grid>{' '}
              <Grid item xs={2} p={2}>
                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  sx={{
                    height: '100%',
                    backgroundColor: '#7B1FA2',
                    textTransform: 'none',
                    borderRadius: '8px',
                    width: '150px',
                    paddingX: 2,
                    float: 'right',
                    '&:hover': {
                      backgroundColor: '#5E167A',
                    },
                  }}
                  startIcon={<Add />}
                  disabled={isEditMode}
                  onClick={() => setOpenAddCustomer(true)}
                >
                  New User
                </Button>
              </Grid>
            </>
          )}
          <Grid item xs={12} mb={2}>
            <ServicesListComp
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              showSnackbar={showSnackbar}
              isEditMode={isEditMode}
            />
          </Grid>
          <CreateUserModal
            open={openAddCustomer}
            onClose={() => setOpenAddCustomer(false)}
            onSaved={handleAddedCustomer}
          />
          {/* Booking Details */}
          <Grid item xs={6}>
            <Typography color="gray">Booking Date</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography color="gray">Booking Slot</Typography>
          </Grid>
          <Grid item xs={6}>
            {/* Themed picker: the old native input's calendar popup followed
                the OS accent color (red) and could not be styled. */}
            <ThemedDateField
              value={bookingDate || ''}
              onChange={(newDate) => setBookingDate(newDate)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <ToggleButtonGroup
              value={selectedSlot}
              exclusive
              onChange={(e, newSlot) => setSelectedSlot(newSlot)}
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #666',
                background: 'transparent',
              }}
            >
              {['FIRST_HALF', 'SECOND_HALF'].map((slot) => (
                <ToggleButton
                  key={slot}
                  value={slot}
                  sx={{
                    flex: 1,
                    color: '#8A8D91',
                    fontWeight: 500,
                    textTransform: 'none',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    background: selectedSlot === slot ? '#000' : 'transparent',
                    '&.Mui-selected': {
                      background: '#000',
                      color: '#8A8D91',
                    },
                    '&:not(:last-of-type)': {
                      borderRight: '1px solid #666',
                    },
                    '&:hover': {
                      background: selectedSlot === slot ? '#000' : '#1a1a1a',
                    },
                  }}
                >
                  {slot === 'FIRST_HALF' ? 'First Half' : 'Second Half'}
                  <AccessTimeOutlined sx={{ fontSize: 16 }} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          <ServiceAddressComp
            selectedUser={selectedUser}
            selectedAddress={selectedAddress}
            onReload={reloadAddress}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 5,
              marginBottom: 1,
              width: '100%',
              gap: 2,
            }}
          >
            <SecondaryButton title="Cancel" onClick={resetBooking} />
            <PrimaryButton
              title={isEditMode ? 'Save' : 'Save'}
              onClick={handleCreateBooking}
              disabled={isLoading}
            />
          </Box>
        </Grid>{' '}
      </Box>{' '}
    </Box>
  );
};

export default AddBooking;

const ServiceAddressComp = ({
  selectedUser,
  selectedAddress,
  onReload,
}: {
  selectedUser: IUser | null;
  selectedAddress: IUserAddress | null;
  onReload: (updatedAddress: IUserAddress) => void;
}) => {
  const [isEditMode, setOpenEditModal] = useState<boolean>(false);
  const [isAddMode, setOpenAddModal] = useState<boolean>(false);
  return (
    <>
      {' '}
      <Grid item xs={12} mt={2}>
        <Typography color="gray">Service Address</Typography>
      </Grid>
      {/* Address Section */}
      <Grid item xs={10}>
        {selectedUser && selectedAddress ? (
          <>
            <Typography variant="body2" sx={{ color: '#ccc' }}>
              {selectedAddress?.house &&
                `${capitalizeWords(selectedAddress.house)}, `}
              {selectedAddress?.street &&
                capitalizeWords(selectedAddress.street)}
              <br />
              {selectedAddress?.city && capitalizeWords(selectedAddress.city)}
              <br />
              {selectedAddress?.landmark &&
                `Landmark: ${capitalizeWords(selectedAddress.landmark)}`}
              <br />
              {selectedAddress?.state &&
                capitalizeWords(selectedAddress.state)}{' '}
              - {selectedAddress?.zipcode}
            </Typography>

            {/* Label + Edit Icon in Dark Style */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 1.2,
                px: 1.5,
                py: 0.75,
                bgcolor: 'rgba(255, 255, 255, 0.05)', // soft dark bg
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2,
                width: 'fit-content',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  bgcolor: '#388e3c', // muted green
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {capitalizeWords(selectedAddress.saveAs)}
              </Typography>

              <IconButton
                size="small"
                sx={{
                  color: '#aaa',
                  p: '4px',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#9C27B0', // subtle red on hover
                  },
                }}
                onClick={() => setOpenEditModal(true)}
              >
                <EditNote fontSize="small" />
              </IconButton>
            </Box>
          </>
        ) : (
          <Typography color="gray" variant="caption">
            Address Not Found
          </Typography>
        )}
      </Grid>
      <Grid item xs={2}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#7B1FA2',
            textTransform: 'none',
            height: '65px',
            borderRadius: '8px',
            float: 'right',
            mr: 2,
            width: '150px',
            paddingX: 2,
            '&:hover': {
              backgroundColor: '#5E167A',
            },
          }}
          startIcon={selectedAddress ? <Edit /> : <Add />}
          onClick={() => setOpenAddModal(true)}
        >
          {'Add Address'}
        </Button>
      </Grid>
      {selectedUser && selectedUser?._id && (
        <>
          <EditAddressDialog
            open={isEditMode}
            onClose={() => setOpenEditModal(false)}
            exisitingAddress={selectedAddress}
            onSave={onReload}
            userId={selectedUser._id}
          />
          <EditAddressDialog
            open={isAddMode}
            onClose={() => setOpenAddModal(false)}
            onSave={onReload}
            userId={selectedUser._id}
          />
        </>
      )}
    </>
  );
};

interface ServicesListCompProps {
  selectedServices: { service: IService; serviceDetails: any }[];
  setSelectedServices: React.Dispatch<
    React.SetStateAction<{ service: IService; serviceDetails: any }[]>
  >;
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
  ) => void;
  isEditMode?: boolean;
}
const sortServices = (services: IService[]) => {
  return services.sort((a, b) => {
    const order = {
      SERVICE: 0,
      REPAIR: 1,
      INSTALLATION: 2,
      COMPRESSOR: 3,
      GAS_CHARGING: 4,
      COMMERCIAL_AC: 5,
      AMC: 6,
      COPPER_PIPING: 7,
      OTHER: 8,
    };

    return (
      order[a.key as keyof typeof order] - order[b.key as keyof typeof order]
    );
  });
};

const resolveServiceFromCatalog = (
  key: string,
  catalog: IService[] | null,
  hints: (IService | null | undefined)[] = [],
): IService => {
  const fromCatalog = catalog?.find((service) => service.key === key);
  if (fromCatalog?._id) return fromCatalog;

  const fromHint = hints.find((service) => service?.key === key && service?._id);
  if (fromHint) return fromHint;

  return { key } as IService;
};

const ServicesListComp: React.FC<ServicesListCompProps> = ({
  selectedServices,
  setSelectedServices,
  showSnackbar,
  isEditMode = false,
}) => {
  const [currDirty, setCurrDirty] = useState<IService | null>(null);
  const [services, setServices] = useState<IService[] | null>(null);
  const [tempServiceDetails, setTempServiceDetails] = useState<any>(null);
  const [showServices, setShowServices] = useState<boolean>(isEditMode);
  // The Job Type -> Appliance -> Component tree comes from the Service-Booking
  // catalog. Only PRIMARY (booking-based) enquiries are offered here.
  const [catalogComponents, setCatalogComponents] = useState<SbComponent[]>([]);
  const [catalogAppliances, setCatalogAppliances] = useState<SbAppliance[]>([]);
  const [primaryKeys, setPrimaryKeys] = useState<string[] | null>(null);
  const [acTypeOptions, setAcTypeOptions] = useState<IntakeOption[]>([]);
  const [problemOptions, setProblemOptions] = useState<IntakeOption[]>([]);
  useEffect(() => {
    fetchServices();
    getBookingComponents()
      // keep "Other" (triggersIntakeForm) — it should appear in every pool
      .then((comps) =>
        setCatalogComponents((comps || []).filter((c) => c.isActive !== false)),
      )
      .catch(() => setCatalogComponents([]));
    getBookingAppliances()
      .then((apps) => setCatalogAppliances((apps || []).filter((a) => a.isActive !== false)))
      .catch(() => setCatalogAppliances([]));
    getBookingJobTypes('primary')
      .then((jts) =>
        setPrimaryKeys((jts || []).map((j) => String(j.key).toUpperCase())),
      )
      .catch(() => setPrimaryKeys(null));
    getBookingAttributeOptions('ac_type')
      .then((o) => setAcTypeOptions((o || []).map((x) => ({ value: x.value, label: x.label }))))
      .catch(() => setAcTypeOptions([]));
    getBookingAttributeOptions('problem_list')
      .then((o) => setProblemOptions((o || []).map((x) => ({ value: x.value, label: x.label }))))
      .catch(() => setProblemOptions([]));
  }, []);

  const fetchServices = async () => {
    try {
      const response = await GETServices({ limit: 100, page: 1 });
      setServices(sortServices(response.data || []));
    } catch (err) {
      console.log('#ERROR !while fetching services');
    }
  };

  const getServiceRecord = (
    key: string,
    ...hints: (IService | null | undefined)[]
  ) => resolveServiceFromCatalog(key, services, [currDirty, ...hints]);

  const isEmptyServiceDetails = (details: any) => {
    if (!details) return true;

    // completely empty {}
    if (Object.keys(details).length === 0) return true;

    // COMMERCIAL_AC case
    if (
      'acList' in details &&
      'services' in details &&
      Object.keys(details.acList || {}).length === 0 &&
      (details.services || []).length === 0
    ) {
      return true;
    }

    if (
      'quantity' in details &&
      details.quantity === 0 &&
      (!details.comments || details.comments.trim() === '')
    ) {
      return true;
    }

    if ('problem' in details || 'comments' in details) {
      const problem = String(details.problem || '').trim();
      const comments = String(details.comments || '').trim();
      return !problem && !comments;
    }

    const values = Object.values(details);
    if (
      values.length > 0 &&
      values.every((val) => !val || Number(val) === 0)
    ) {
      return true;
    }

    return false;
  };

  const toggleService = (service: IService) => {
    setCurrDirty(service);

    setSelectedServices((prev) => {
      const isSelected = prev.some((s) => s.service.key === service.key);

      let updatedList = [...prev];

      if (isSelected) {
        // ❌ remove if already selected
        updatedList = updatedList.filter((s) => s.service.key !== service.key);
      } else {
        // ✅ add with default structure
        let newService;

        if (service.key === 'COMMERCIAL_AC') {
          newService = {
            service,
            serviceDetails: { acList: {}, services: [] },
          };
        } else if (service.key === 'AMC' || service.key === 'COPPER_PIPING') {
          newService = {
            service,
            serviceDetails: {
              quantity: 0,
              place: 'Residential',
              comments: '',
            },
          };
        } else if (service.key === 'OTHER') {
          newService = {
            service,
            serviceDetails: {
              problem: '',
              comments: '',
              acType: '',
              quantity: 1,
            },
          };
        } else {
          newService = {
            service,
            serviceDetails: {},
          };
        }

        updatedList.push(newService);
      }

      // 🔥 FINAL STEP: remove all empty serviceDetails
      return updatedList.filter(
        (item) => !isEmptyServiceDetails(item.serviceDetails),
      );
    });
  };

  const handleCommercialACUpdate = (
    acList: Record<string, number>,
    services: string[],
  ) => {
    const isAcListEmpty = !acList || Object.keys(acList).length === 0;

    const isServicesEmpty = !services || services.length === 0;
    if (isAcListEmpty && isServicesEmpty) {
      showSnackbar('Please Select Any Value', 'error');
      return;
    }
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'COMMERCIAL_AC');

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'COMMERCIAL_AC'
            ? {
                service: getServiceRecord('COMMERCIAL_AC', s.service),
                serviceDetails: {
                  acList,
                  services,
                },
              }
            : s,
        );
      }

      return [
        ...prev,
        {
          service: getServiceRecord('COMMERCIAL_AC'),
          serviceDetails: {
            acList,
            services,
          },
        },
      ];
    });
  };
  const updateAMCDetails = (
    quantity: number,
    place: 'Residential' | 'Commercial',
    comments: string,
  ) => {
    const isQuantityInvalid = !quantity || quantity <= 0;

    if (isQuantityInvalid) {
      showSnackbar('Quantity must be greater than 0', 'error');
      return;
    }

    // ✅ Update only if valid
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'AMC');

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'AMC'
            ? {
                service: getServiceRecord('AMC', s.service),
                serviceDetails: { quantity, place, comments },
              }
            : s,
        );
      }

      return [
        ...prev,
        {
          service: getServiceRecord('AMC'),
          serviceDetails: { quantity, place, comments },
        },
      ];
    });
  };

  const updateCopperPipingDetails = (
    quantity: number,
    place: 'Residential' | 'Commercial',
    comments: string,
  ) => {
    const isQuantityInvalid = !quantity || quantity <= 0;

    if (isQuantityInvalid) {
      showSnackbar('Quantity must be greater than 0', 'error');
      return;
    }

    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'COPPER_PIPING');

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'COPPER_PIPING'
            ? {
                service: getServiceRecord('COPPER_PIPING', s.service),
                serviceDetails: { quantity, place, comments },
              }
            : s,
        );
      }

      // ❗ If not exists → add it
      return [
        ...prev,
        {
          service: getServiceRecord('COPPER_PIPING'),
          serviceDetails: { quantity, place, comments },
        },
      ];
    });
  };

  const handleProblemReportUpdate = (problem: string, comments: string, acType: string) => {
    const isAcTypeEmpty = !acType || acType.trim() === '';
    const isProblemEmpty = !problem || problem.trim() === '';
    const isCommentsEmpty = !comments || comments.trim() === '';

    if (isAcTypeEmpty) {
      showSnackbar('Please select an AC type', 'error');
      return;
    }

    if (isProblemEmpty) {
      showSnackbar('Please select or enter problem', 'error');
      return;
    }

    if (isCommentsEmpty) {
      showSnackbar('Please enter comments', 'error');
      return;
    }

    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'OTHER');
      const otherService = getServiceRecord(
        'OTHER',
        exists ? prev.find((s) => s.service.key === 'OTHER')?.service : undefined,
      );

      if (!otherService._id) {
        showSnackbar('Other service is not configured. Please contact admin.', 'error');
        return prev;
      }

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'OTHER'
            ? {
                service: otherService,
                serviceDetails: { problem, comments, acType, quantity: 1 },
              }
            : s,
        );
      }

      return [
        ...prev,
        {
          service: otherService,
          serviceDetails: { problem, comments, acType, quantity: 1 },
        },
      ];
    });
  };
  // Only primary (booking-based) enquiries are bookable here. Use catalog
  // primary keys when available, else a safe default so tabs never vanish.
  const effectivePrimary =
    primaryKeys && primaryKeys.length
      ? primaryKeys
      : ['SERVICE', 'REPAIR', 'INSTALLATION'];
  // Filter to primary, then DEDUPE by key (legacy + mirrored services can share
  // a key) — prefer the catalog-mirrored one (sbJobTypeKey set).
  const seenKeys = new Set<string>();
  const visibleServices = (services || [])
    .filter((s) => effectivePrimary.includes(s.key))
    .sort(
      (a, b) =>
        Number(!!(b as any).sbJobTypeKey) - Number(!!(a as any).sbJobTypeKey),
    )
    .filter((s) => {
      if (seenKeys.has(s.key)) return false;
      seenKeys.add(s.key);
      return true;
    });
  return (
    <Grid
      container
      spacing={1}
      sx={{
        borderRadius: 3,
        backgroundColor: '#232323',
        padding: 2,
      }}
    >
      <Grid item xs={12} mb={1}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',

            height: 60,
          }}
        >
          <Typography gutterBottom color="#8A8D91" flex={2}>
            All Services
          </Typography>

          {!showServices && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#7B1FA2',
                textTransform: 'none',
                height: '100%',
                borderRadius: '8px',
                width: '150px',
                paddingX: 2,
                '&:hover': {
                  backgroundColor: '#5E167A',
                },
              }}
              startIcon={<Add />}
              onClick={() => setShowServices((prev) => !prev)}
            >
              {'Add Services'}
            </Button>
          )}
        </Box>
      </Grid>

      {showServices && (
        <>
          <Grid container spacing={1} sx={{ p: 1 }}>
            {visibleServices?.map((service) => {
              const isSelected = selectedServices.some(
                (s) => s.service.key === service.key,
              );
              const isCurrent = currDirty?.key === service.key;
              return (
                <Grid item key={service._id} md={3} p={1}>
                  <Button
                    variant={isCurrent ? 'contained' : 'outlined'}
                    disableElevation
                    fullWidth
                    color="inherit"
                    onClick={() => toggleService(service)}
                    disabled={isSelected && !isCurrent}
                    sx={{
                      minWidth: 120,
                      minHeight: 38,
                      borderRadius: '8px',
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      fontSize: '0.97rem',
                      px: 3,
                      py: 1,
                      margin: 0,
                      backgroundColor: isCurrent ? '#7B1FA2' : 'transparent',
                      border: isCurrent ? 'none' : '2px solid #7B1FA2',
                      color: '#fff',
                      transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: isCurrent
                          ? '#5E167A'
                          : 'rgba(123,31,162,0.12)',
                        border: isCurrent ? 'none' : '2px solid #A855F7',
                        color: '#fff',
                        boxShadow: 'none',
                      },
                      '&:disabled': {
                        color: '#888',
                        borderColor: '#888',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    {capitalizeWords(service.key.replace('_', ' '))}
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          {currDirty && (
            <Grid item xs={8}>
              {currDirty &&
                (effectivePrimary.includes(currDirty.key) ||
                  [
                    'COMPRESSOR',
                    'GAS_CHARGING',
                  ].includes(currDirty.key)) && (
                  <AcList
                    details={
                      selectedServices.find(
                        (s) => s.service.key === currDirty.key,
                      )?.serviceDetails ||
                      tempServiceDetails ||
                      {}
                    }
                    appliances={catalogAppliances}
                    components={catalogComponents}
                    fallbackItems={acItems}
                    acTypeOptions={acTypeOptions}
                    problemOptions={problemOptions}
                    service={currDirty}
                    setSelectedServices={setSelectedServices}
                    setCurrDirty={setCurrDirty}
                    showSnackbar={showSnackbar}
                  />
                )}{' '}
              {currDirty?.key === 'COMMERCIAL_AC' && (
                <CommercialACForm
                  existingDetails={
                    selectedServices.find(
                      (s) => s.service.key === 'COMMERCIAL_AC',
                    )?.serviceDetails || { acList: {}, services: [] }
                  }
                  onUpdate={handleCommercialACUpdate}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}
              {currDirty?.key === 'AMC' && (
                <AMCForm
                  existingDetails={
                    selectedServices.find((s) => s.service.key === 'AMC')
                      ?.serviceDetails || {
                      quantity: 0,

                      place: 'Residential',
                      comments: '',
                    }
                  }
                  label="Amc"
                  onUpdate={updateAMCDetails}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}
              {currDirty?.key === 'COPPER_PIPING' && (
                <AMCForm
                  existingDetails={
                    selectedServices.find(
                      (s) => s.service.key === 'COPPER_PIPING',
                    )?.serviceDetails || {
                      quantity: 0,

                      place: 'Residential',
                      comments: '',
                    }
                  }
                  label="Copper Piping"
                  onUpdate={updateCopperPipingDetails}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}{' '}
              {currDirty?.key === 'OTHER' && (
                <ProblemReportForm
                  existingDetails={
                    selectedServices.find((s) => s.service.key === 'OTHER')
                      ?.serviceDetails || { problem: '', comments: '', acType: '' }
                  }
                  onUpdate={handleProblemReportUpdate}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}
            </Grid>
          )}

          <Grid item xs={12} mt={1}>
            <ViewServiceDetails
              services={selectedServices}
              setCurrDirty={setCurrDirty}
              setSelectedServices={setSelectedServices}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

// serviceDetails key for primary enquiries = "<Appliance> ▸ <Component>".
// On submit this splits into serviceType (appliance) + acType (component).
export const APPL_SEP = ' ▸ ';
// Reserved key on a primary service's details holding the "Other" intakes.
export const OTHER_KEY = '__others__';
export type OtherEntry = {
  appliance: string;
  acType?: string; // chosen Appliance-Type (ac_type list)
  problems?: string[]; // selected Problem list values
  detail?: string; // free-text Additional Detail
};

interface AcListProps {
  details: Record<string, any>;
  service: IService;
  setSelectedServices: React.Dispatch<React.SetStateAction<any>>;
  setCurrDirty: React.Dispatch<React.SetStateAction<any>>;
  appliances: SbAppliance[];
  components: SbComponent[];
  fallbackItems: { name: string }[]; // legacy AC list if catalog is empty
  acTypeOptions: { value: string; label: string }[];
  problemOptions: { value: string; label: string }[];
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
  ) => void;
}
const AcList: React.FC<AcListProps> = ({
  details,
  service,
  setSelectedServices,
  setCurrDirty,
  appliances,
  components,
  fallbackItems,
  acTypeOptions,
  problemOptions,
  showSnackbar,
}) => {
  const [tempServiceDetails, setTempServiceDetails] = useState<Record<string, any>>(
    details || {},
  );

  useEffect(() => {
    setTempServiceDetails(details || {});
  }, [details, service]);

  // Appliances offered under this enquiry type (Step 2 of the diagram).
  const jobTypeKey = String(service.key || '').toLowerCase();
  const applianceTabs = appliances.filter(
    (a) =>
      !a.supportedJobTypeKeys?.length ||
      a.supportedJobTypeKeys.includes(jobTypeKey),
  );

  const [activeAppliance, setActiveAppliance] = useState<SbAppliance | null>(
    applianceTabs[0] || null,
  );
  useEffect(() => {
    if (!activeAppliance && applianceTabs.length) setActiveAppliance(applianceTabs[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applianceTabs.length]);

  const usingCatalog = applianceTabs.length > 0 && components.length > 0;

  // Pool for the selected appliance (Step 3). "Other" is split out — it opens
  // the intake form instead of a counter. Falls back to the legacy flat list.
  const poolItems: { name: string; isOther?: boolean; icon?: string }[] =
    usingCatalog
      ? components
          .filter((c) => c.applianceKeys?.includes(activeAppliance?.key || ''))
          .map((c) => ({
            name: c.label,
            isOther: !!c.triggersIntakeForm,
            icon: c.icon,
          }))
      : fallbackItems;
  const componentItems = poolItems.filter((i) => !i.isOther);
  const hasOther = poolItems.some((i) => i.isOther);

  // Storage key: namespaced by appliance so shared components (e.g. Outdoor
  // Unit used by two appliances) stay separate lines.
  const storeKey = (componentName: string) =>
    usingCatalog && activeAppliance
      ? `${activeAppliance.label}${APPL_SEP}${componentName}`
      : componentName;

  // ── "Other" intake (per appliance) ────────────────────────────────────────
  const othersArr: OtherEntry[] = Array.isArray(tempServiceDetails[OTHER_KEY])
    ? tempServiceDetails[OTHER_KEY]
    : [];
  const currentOther: OtherEntry =
    othersArr.find((o) => o.appliance === activeAppliance?.label) || {
      appliance: activeAppliance?.label || '',
      acType: '',
      problems: [],
      detail: '',
    };
  const otherIsEmpty = (o: OtherEntry) =>
    !o.acType && !(o.problems && o.problems.length) && !o.detail;
  const patchOther = (patch: Partial<OtherEntry>) => {
    setTempServiceDetails((prev) => {
      const arr: OtherEntry[] = Array.isArray(prev[OTHER_KEY]) ? [...prev[OTHER_KEY]] : [];
      const idx = arr.findIndex((o) => o.appliance === activeAppliance?.label);
      const base: OtherEntry = idx >= 0 ? arr[idx] : { appliance: activeAppliance?.label || '' };
      const merged = { ...base, ...patch };
      if (idx >= 0) {
        if (otherIsEmpty(merged)) arr.splice(idx, 1);
        else arr[idx] = merged;
      } else if (!otherIsEmpty(merged)) {
        arr.push(merged);
      }
      return { ...prev, [OTHER_KEY]: arr };
    });
  };
  const toggleProblem = (value: string) => {
    const set = new Set(currentOther.problems || []);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    patchOther({ problems: Array.from(set) });
  };

  const handleCountChange = (key: string, newCount: number) => {
    setTempServiceDetails((prev) => {
      if (newCount <= 0) {
        const { [key]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: newCount };
    });
  };

  const handleSave = () => {
    // counts (numbers only — removeZeroQuantityEntries drops the __others__ array)
    const counts = removeZeroQuantityEntries(tempServiceDetails);
    const others = othersArr.filter((o) => !otherIsEmpty(o));
    if (Object.keys(counts).length === 0 && others.length === 0) {
      showSnackbar('Please select at least one item', 'error');
      return;
    }
    const cleanedDetails: Record<string, any> = { ...counts };
    if (others.length) cleanedDetails[OTHER_KEY] = others;

    setSelectedServices(
      (prevServices: { service: IService; serviceDetails: any }[]) => {
        const updated = prevServices.map((s) =>
          s.service.key === service.key
            ? { ...s, serviceDetails: cleanedDetails }
            : s,
        );
        return updated.some((s) => s.service.key === service.key)
          ? updated
          : [...updated, { service, serviceDetails: cleanedDetails }];
      },
    );
    setCurrDirty(null);
  };

  const selectedCount =
    Object.values(removeZeroQuantityEntries(tempServiceDetails)).reduce(
      (n: number, v) => n + (Number(v) || 0),
      0,
    ) + othersArr.filter((o) => !otherIsEmpty(o)).length;
  const PURPLE = '#A855F7';
  const AMBER = '#E0A82E';

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg,#1f1f23 0%,#191919 100%)',
        p: 2.5,
        borderRadius: '14px',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Step 2 — appliance sub-tabs */}
      {usingCatalog && (
        <>
          <Typography
            sx={{ color: '#8A8D91', fontSize: '0.78rem', fontWeight: 600, letterSpacing: 0.6, mb: 1 }}
          >
            CHOOSE EQUIPMENT
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
            {applianceTabs.map((a) => {
              const isActive = activeAppliance?.key === a.key;
              return (
                <Box
                  key={a.key}
                  component="button"
                  onClick={() => setActiveAppliance(a)}
                  sx={{
                    cursor: 'pointer',
                    px: 2,
                    py: 1,
                    borderRadius: '999px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: isActive ? '#fff' : '#cfcfcf',
                    background: isActive
                      ? 'linear-gradient(135deg,#7B1FA2,#A855F7)'
                      : 'transparent',
                    border: `1.5px solid ${isActive ? 'transparent' : 'rgba(168,85,247,0.5)'}`,
                    boxShadow: isActive ? '0 4px 14px rgba(123,31,162,0.45)' : 'none',
                    transition: 'all .2s ease',
                    '&:hover': {
                      borderColor: PURPLE,
                      color: '#fff',
                    },
                  }}
                >
                  {a.label}
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {/* Step 3 — components for the chosen appliance */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
        }}
      >
        {componentItems.map((ac, index) => {
          const key = storeKey(ac.name);
          const count = Number(tempServiceDetails[key] || 0);
          const active = count > 0;
          const accent = ac.isOther ? AMBER : PURPLE;
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 1.5,
                py: 1.1,
                borderRadius: '12px',
                background: active ? 'rgba(168,85,247,0.10)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${active ? accent : 'rgba(255,255,255,0.08)'}`,
                transition: 'all .18s ease',
                '&:hover': { borderColor: accent },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                <Avatar
                  src={ac.icon || undefined}
                  variant="rounded"
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: ac.isOther ? 'rgba(224,168,46,0.18)' : 'rgba(168,85,247,0.18)',
                    color: accent,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  }}
                >
                  {ac.name?.[0]?.toUpperCase()}
                </Avatar>
                <Typography
                  noWrap
                  sx={{ color: active ? '#fff' : '#d6d6d6', fontSize: '0.98rem', fontWeight: 500 }}
                >
                  {ac.name}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#161616',
                  borderRadius: '999px',
                  px: 0.4,
                  py: 0.2,
                }}
              >
                <IconButton
                  size="small"
                  disabled={count <= 0}
                  onClick={() => handleCountChange(key, Math.max(0, count - 1))}
                  sx={{ color: count <= 0 ? '#555' : '#fff', p: 0.4 }}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <Box
                  sx={{
                    minWidth: 24,
                    textAlign: 'center',
                    color: active ? accent : '#aaa',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  {count}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleCountChange(key, count + 1)}
                  sx={{ color: '#fff', p: 0.4 }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* "Other" → intake form (not a counter). Step 4 of the diagram. */}
      {hasOther && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.75,
            borderRadius: '12px',
            background: 'rgba(224,168,46,0.06)',
            border: `1px solid ${
              !otherIsEmpty(currentOther) ? AMBER : 'rgba(224,168,46,0.35)'
            }`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 28,
                height: 28,
                bgcolor: 'rgba(224,168,46,0.2)',
                color: AMBER,
                fontSize: '0.8rem',
                fontWeight: 700,
              }}
            >
              O
            </Avatar>
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>
              Other — part not listed
            </Typography>
            <Typography sx={{ color: '#8A8D91', fontSize: '0.8rem' }}>
              ({activeAppliance?.label})
            </Typography>
          </Box>

          {/* Appliance / part type */}
          {acTypeOptions.length > 0 && (
            <Box sx={{ mb: 1.25 }}>
              <Typography sx={{ color: '#8A8D91', fontSize: '0.75rem', mb: 0.5 }}>
                Appliance / part type
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {acTypeOptions.map((o) => {
                  const sel = currentOther.acType === o.value;
                  return (
                    <Chip
                      key={o.value}
                      label={o.label}
                      size="small"
                      onClick={() => patchOther({ acType: sel ? '' : o.value })}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: sel ? AMBER : '#262626',
                        color: sel ? '#1a1a1a' : '#ddd',
                        fontWeight: sel ? 700 : 500,
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Problem list (multi-select) */}
          {problemOptions.length > 0 && (
            <Box sx={{ mb: 1.25 }}>
              <Typography sx={{ color: '#8A8D91', fontSize: '0.75rem', mb: 0.5 }}>
                Problem(s)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {problemOptions.map((o) => {
                  const sel = (currentOther.problems || []).includes(o.value);
                  return (
                    <Chip
                      key={o.value}
                      label={o.label}
                      size="small"
                      onClick={() => toggleProblem(o.value)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: sel ? PURPLE : '#262626',
                        color: sel ? '#fff' : '#ddd',
                        fontWeight: sel ? 700 : 500,
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Additional detail */}
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Additional detail (optional)"
            value={currentOther.detail || ''}
            onChange={(e) => patchOther({ detail: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                backgroundColor: '#161616',
                borderRadius: '8px',
              },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
            }}
          />
        </Box>
      )}

      {/* Footer — running count + Done */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 2.5,
        }}
      >
        <Typography sx={{ color: '#8A8D91', fontSize: '0.85rem' }}>
          {selectedCount > 0
            ? `${selectedCount} unit${selectedCount > 1 ? 's' : ''} selected`
            : 'No items selected yet'}
        </Typography>
        <Button
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg,#7B1FA2,#A855F7)',
            color: 'white',
            borderRadius: '10px',
            fontWeight: 600,
            textTransform: 'none',
            minWidth: 110,
            px: 3,
            height: 40,
            boxShadow: '0 4px 14px rgba(123,31,162,0.4)',
            '&:hover': { background: 'linear-gradient(135deg,#6A1B8F,#9333EA)' },
          }}
          onClick={handleSave}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};

const ViewServiceDetails = ({
  services,
  setCurrDirty,
  setSelectedServices,
  readonly = true, // let parent control editability
}: {
  services: { service: IService; serviceDetails: any }[]; // `any` because serviceDetails shape varies by type
  setCurrDirty: React.Dispatch<React.SetStateAction<IService | null>>;
  setSelectedServices: React.Dispatch<React.SetStateAction<any>>;
  readonly?: boolean;
}) => {
  const handleEdit = (service: IService) => setCurrDirty(service);

  const handleDelete = (service: IService) => {
    console.log('service', service);

    setSelectedServices((prev: any) =>
      prev.filter((s: any) => s.service.key !== service.key),
    );
  };

  // Simple map function to turn serviceDetails into row props for ServiceRow:
  const serviceToRowProps = (serviceObj: {
    service: IService;
    serviceDetails: any;
  }) => {
    const { service, serviceDetails } = serviceObj;
    const key = service.key;

    if (
      [
        'SERVICE',
        'REPAIR',
        'INSTALLATION',
        'COMPRESSOR',
        'GAS_CHARGING',
      ].includes(key)
    ) {
      return {
        label: service.key.replace('_', ' '),
        items: Object.entries(removeZeroQuantityEntries(serviceDetails)).map(
          ([type, quantity]) => ({
          label: `${type}`,
          value:
            typeof quantity === 'string' || typeof quantity === 'number'
              ? quantity
              : String(quantity),
        }),
        ),
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'COMMERCIAL_AC') {
      return {
        label: 'Commercial AC',
        items: Object.entries(
          removeZeroQuantityEntries(serviceDetails.acList ?? {}),
        ).map(([type, quantity]) => ({
            label: type,
            value:
              typeof quantity === 'string' || typeof quantity === 'number'
                ? quantity
                : String(quantity),
          })),
        chips: Array.isArray(serviceDetails.services)
          ? serviceDetails.services
          : [],
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'AMC') {
      return {
        label: 'AMC',
        items: [
          { label: serviceDetails.place },
          {
            label: 'Quantity',
            value:
              typeof serviceDetails.quantity === 'string' ||
              typeof serviceDetails.quantity === 'number'
                ? serviceDetails.quantity
                : String(serviceDetails.quantity),
          },
        ],
        comment: serviceDetails.comments,
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'COPPER_PIPING') {
      return {
        label: 'Copper Piping',
        items: [
          { label: serviceDetails.place },
          {
            label: 'Quantity',
            value:
              typeof serviceDetails.quantity === 'string' ||
              typeof serviceDetails.quantity === 'number'
                ? serviceDetails.quantity
                : String(serviceDetails.quantity),
          },
        ],
        comment: serviceDetails.comments,
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'OTHER') {
      return {
        label: 'Other',
        items: [
          { label: 'AC Type', value: serviceDetails.acType || 'N/A' },
          { label: 'Problem', value: serviceDetails.problem },
        ],
        comment: serviceDetails.comments,
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }
    return null;
  };

  const PRIMARY_SERVICE_KEYS = [
    'SERVICE',
    'REPAIR',
    'INSTALLATION',
    'COMPRESSOR',
    'GAS_CHARGING',
  ];

  // Group a primary service's composite-keyed details by appliance for a tidy,
  // readable summary instead of one long crammed line.
  const groupByAppliance = (serviceDetails: Record<string, any>) => {
    const groups: Record<string, { name: string; qty?: number; isOther?: boolean }[]> = {};
    // counted components (removeZeroQuantityEntries drops the __others__ array)
    Object.entries(removeZeroQuantityEntries(serviceDetails || {})).forEach(
      ([key, qty]) => {
        const [appliancePart, componentPart] = String(key).split(APPL_SEP);
        const appliance = componentPart ? appliancePart : 'General';
        const name = componentPart ?? appliancePart;
        if (!groups[appliance]) groups[appliance] = [];
        groups[appliance].push({ name, qty: Number(qty) || 0 });
      },
    );
    // "Other" intakes
    const others: OtherEntry[] = Array.isArray(serviceDetails?.[OTHER_KEY])
      ? serviceDetails[OTHER_KEY]
      : [];
    others.forEach((o) => {
      const appliance = o.appliance || 'General';
      if (!groups[appliance]) groups[appliance] = [];
      const parts = [o.acType, (o.problems || []).join(', '), o.detail].filter(Boolean);
      groups[appliance].push({
        name: `Other${parts.length ? `: ${parts.join(' · ')}` : ''}`,
        isOther: true,
      });
    });
    return Object.entries(groups).map(([appliance, items]) => ({ appliance, items }));
  };

  return (
    <CardContent sx={{ padding: 0 }}>
      {Array.isArray(services) &&
        services.map((serviceObj, idx) => {
          const isPrimary = PRIMARY_SERVICE_KEYS.includes(serviceObj.service.key);

          if (isPrimary) {
            const groups = groupByAppliance(serviceObj.serviceDetails);
            const total = groups.reduce(
              (n, g) => n + g.items.reduce((m, it) => m + (it.qty || 0), 0),
              0,
            );
            return (
              <Box
                key={idx}
                sx={{
                  backgroundColor: '#1c1c1c',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '12px',
                  p: 1.75,
                  my: 1.5,
                  width: '100%',
                }}
              >
                {/* header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#0BE600', fontWeight: 700, fontSize: '1rem' }}>
                      {capitalizeWords(serviceObj.service.key.replace('_', ' '))}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${total} unit${total > 1 ? 's' : ''}`}
                      sx={{
                        backgroundColor: 'rgba(168,85,247,0.18)',
                        color: '#C98BFF',
                        fontWeight: 600,
                        height: 22,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleEdit(serviceObj.service)} sx={{ color: '#A855F7' }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(serviceObj.service)} sx={{ color: '#ff5c6c' }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* one row per appliance */}
                <Stack spacing={1}>
                  {groups.map((g) => (
                    <Box
                      key={g.appliance}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        py: 0.5,
                        borderTop: '1px solid rgba(255,255,255,0.05)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#A855F7',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          minWidth: 96,
                        }}
                      >
                        {g.appliance}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {g.items.map((it, i) => (
                          <Chip
                            key={i}
                            size="small"
                            label={
                              <span>
                                {it.name}
                                {!it.isOther && (
                                  <span style={{ color: '#C98BFF', fontWeight: 700 }}>
                                    {`  ×${it.qty}`}
                                  </span>
                                )}
                              </span>
                            }
                            sx={{
                              backgroundColor: it.isOther ? 'rgba(224,168,46,0.16)' : '#262626',
                              color: it.isOther ? '#E0A82E' : '#e6e6e6',
                              borderRadius: '8px',
                              border: `1px solid ${it.isOther ? 'rgba(224,168,46,0.4)' : 'rgba(255,255,255,0.08)'}`,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            );
          }

          const rowProps = serviceToRowProps(serviceObj);
          return rowProps ? (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <ServiceRow {...rowProps} />
            </Box>
          ) : null;
        })}
    </CardContent>
  );
};

const ViewCustomer = ({ Customer }: { Customer: IUser }) => {
  return (
    <>
      {' '}
      <Grid container justifyContent="center">
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          sx={{
            backgroundColor: '#232323',
            color: '#FFFFFF',
            borderRadius: 2,
            p: 2,
            // boxShadow: 3,
            margin: 1,
          }}
        >
          <Box sx={{ width: '80%', margin: 'auto' }}>
            <Box>
              <Grid container spacing={2} alignItems="center">
                {/* Full Name & Contact Number */}
                <Grid item xs={12}>
                  <Box display="flex">
                    <Typography flex={1} color="#8A8D91">
                      Full Name
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {Customer?.name || 'N/A'}
                    </Typography>{' '}
                    <Typography flex={1} color="#8A8D91">
                      Contact Number
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {formatPhoneNumber(Customer?.phoneNumber)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Email & Customer Type */}
                <Grid item xs={12}>
                  <Box display="flex">
                    <Typography flex={1} color="#8A8D91">
                      Email Address
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {Customer?.email || 'N/A'}
                    </Typography>{' '}
                    <Typography flex={1} color="#8A8D91">
                      Customer Type
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {Customer?.type
                        ? capitalizeWords(Customer?.type).replace('_', ' ')
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
